import { useState, useEffect, useRef } from 'react';

import useQuickActions from '@hooks/useQuickActions';
import ArrowUp from '@lib/core/src/assets/buttons-icons/arrow-up-line.svg?react';
import ThreadsIcon from '@lib/core/src/assets/buttons-icons/side-bar-line.svg?react';
import palette from '@lib/core/src/theme/palette';
import { IconButton, Typography } from '@mui/material';
import { RiCloseLine } from '@remixicon/react';

import {
  ThreadsButtonRedDotCount,
  ThreadsButtonRedDotCountText
} from '@/components/AskMeAnything/ChatStyle';
import useChatLogic from '@/components/AskMeAnything/hooks/useChatLogic';
import { useChatActions } from '@/store/chat/chatStore';
import { useSelectedMeetings, useSelectedMeetingsActions } from '@/store/selectedMeetingsStore';
import { useIsSmallScreen } from '@/utils/mediaUtil';

import {
  Container,
  ButtonContainer,
  TextInput,
  BottomContainer,
  HeaderContainer,
  QuickActionsButton,
  ContainerEnvelope,
  RemoveMeetingsButton,
  StartRow,
  StartRowText
} from './IndependentChatInputStyles';

interface IndependentChatInputProps {
  isRemoveButton?: boolean;
  isHeader?: boolean;
  isChatPage?: boolean;
}

const IndependentChatInput = ({
  isRemoveButton = true,
  isHeader = true,
  isChatPage = false
}: IndependentChatInputProps) => {
  const { setIsChatOpen, setIsThreadsOpen } = useChatActions();
  const { handleSubmit, unseenThreadsCount, handleOpenQuickActions } = useChatLogic();
  const [prompt, setPrompt] = useState('');
  const isPromptValid = prompt.length > 0 && prompt.trim() !== '';
  const isSmallScreen = useIsSmallScreen();
  const { quickActions, refetchQuickActions } = useQuickActions({ count: -1 });
  const selectedMeetings = useSelectedMeetings();
  const { clearSelectedMeetings } = useSelectedMeetingsActions();

  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    refetchQuickActions();
  }, [refetchQuickActions]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const openChat = (prompt: string) => {
    if (!isPromptValid) return;
    if (isChatPage) {
      handleSubmit(prompt.trim(), true);
    } else {
      setIsChatOpen(true);
      handleSubmit(prompt.trim());
      setPrompt('');
    }
  };

  const handleOpenThreads = () => {
    setIsThreadsOpen(true);
    setIsChatOpen(true);
  };

  const focusInput = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (
      target.closest('button') ||
      target.closest('svg') ||
      target.closest('input') ||
      target.closest('textarea')
    ) {
      return;
    }
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!isSmallScreen) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        openChat(prompt);
      }
    }
  };

  return (
    <ContainerEnvelope sx={{ height: isHeader ? '112px' : '68px' }}>
      <Container onClick={focusInput}>
        {isHeader && (
          <HeaderContainer>
            <StartRow>
              {!isChatPage && (
                <IconButton sx={{ backgroundColor: palette.grey[200] }} onClick={handleOpenThreads}>
                  <ThreadsIcon
                    style={{ stroke: palette.grey[500], height: '14px', width: '14px' }}
                  />
                  {unseenThreadsCount > 0 && (
                    <ThreadsButtonRedDotCount>
                      <ThreadsButtonRedDotCountText>
                        {unseenThreadsCount > 9 ? '9+' : unseenThreadsCount}
                      </ThreadsButtonRedDotCountText>
                    </ThreadsButtonRedDotCount>
                  )}
                </IconButton>
              )}
              <StartRowText>What can I help you with today?</StartRowText>
            </StartRow>
            <QuickActionsButton onClick={() => handleOpenQuickActions(isChatPage)}>
              <Typography sx={{ fontSize: '12px', color: palette.purple[300] }}>
                <span style={{ fontWeight: 700 }}>{quickActions.length}</span> Quick Actions
              </Typography>
            </QuickActionsButton>
          </HeaderContainer>
        )}
        <TextInput
          ref={inputRef}
          minRows={1}
          maxRows={2}
          placeholder="Ask me anything..."
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <BottomContainer>
          {selectedMeetings.length > 0 && isRemoveButton && (
            <RemoveMeetingsButton onClick={() => clearSelectedMeetings()}>
              {selectedMeetings.length} {selectedMeetings.length === 1 ? 'Meeting' : 'Meetings'}
              <RiCloseLine size={13} />
            </RemoveMeetingsButton>
          )}
          <IconButton onClick={() => openChat(prompt)} disabled={!isPromptValid}>
            <ButtonContainer
              sx={{ backgroundColor: isPromptValid ? palette.black : palette.grey[200] }}
            >
              <ArrowUp
                style={{
                  color: isPromptValid ? palette.white : palette.purple[300],
                  height: '16px',
                  width: '16px',
                  marginBottom: '2px'
                }}
              />
            </ButtonContainer>
          </IconButton>
        </BottomContainer>
      </Container>
    </ContainerEnvelope>
  );
};

export default IndependentChatInput;
